import React, {useState, useEffect} from 'react';
import {PanoramaScene} from "./PanoramaScene";
import {ApiClient, PanoramaTour} from "./api/apiClient";
import {BrowserUI} from "./components/ui/browser/BrowserUI";
import {Backdrop, CircularProgress, Stack, Typography} from "@mui/material";
import {Vector3} from "three";
import {launchPanoramaChangeAnimations, resetCamera, stopHouseTour} from "./components/common";
import {EntryScreen, StartTour} from "./components/entry/EntryScreen";
import {isInHostFrame, ownPageUrl} from "./components/entry/hostFrame";
import {useViewportWidth, ViewportWidth} from "./components/ui/browser/useNavLayout";

export interface AppProps {
    contentUrl: string;
}

type TourStage = "entry" | "tour";

function App(props: AppProps) {

    const [panoramaTour, setPanoramaTour] = useState<PanoramaTour>();
    const [isVrMode, setIsVrMode] = useState(false);
    const [userSelectedPanorama, setUserSelectedPanorama] = useState<undefined | string>(undefined);
    const [loading, setLoading] = useState(true);

    // Only a framed viewer gets the entry screen: a page of our own was opened for the tour itself.
    const [stage, setStage] = useState<TourStage>(isInHostFrame() ? "entry" : "tour");

    const urlParams = new URLSearchParams(window.location.search);
    let defaultPanorama = urlParams.get('defaultsky');

    const apiClient = new ApiClient(props.contentUrl);

    useEffect(() => {
        apiClient.getPanoramaTour().then((result) => {
            if (result) {
                setPanoramaTour(result);
            }
        });
    }, []);

    const getPanoramaData = (panoramaName: string) => {
        const panoramaData = panoramaTour?.panoramas.find(p => p.name === panoramaName);

        if (!panoramaData) {
            throw new Error("Panorama not found: " + panoramaName);
        }
        return panoramaData;
    }

    const changePanorama = (newPanorama: string, zoomTowards?: Vector3, continueTour?: boolean) => {
        console.log("Changing to: " + newPanorama);

        const newPanoramaData = getPanoramaData(newPanorama);

        // Launch animations for changing panorama. If zoomTowards is set, zoom towards that direction. Otherwise, use default look at direction.
        launchPanoramaChangeAnimations(zoomTowards, zoomTowards ? undefined : newPanoramaData.defaultLookAtDirection);

        // Stop the tour if change was caused by user interaction.
        if (!continueTour) {
            stopHouseTour();
        }

        // Change the panorama on small delay, so animations can finish.
        setTimeout(() => setUserSelectedPanorama(newPanorama), 700);
    };

    const startPanorama = panoramaTour && chooseStartPanorama(panoramaTour, defaultPanorama);
    const startRoomImages = panoramaTour ? entryRoomImageCandidates(panoramaTour, startPanorama) : [];

    // The panorama the live tour is showing: whichever room the user walked to, or the start view until they move.
    const currentPanorama = userSelectedPanorama ?? startPanorama;

    const viewportWidth = useViewportWidth();

    // A frame this narrow is a dialog on someone's page, and all the room there is.
    const startTour: StartTour = viewportWidth === ViewportWidth.Narrow
        ? {target: "own-page", href: ownPageUrl()}
        : {target: "in-frame", onStart: () => setStage("tour")};

    useEffect(() => {
        // When the tour is initially loaded, point the camera at the default look at direction.
        if (!loading && startPanorama && panoramaTour) {
            const startPanoramaData = getPanoramaData(startPanorama);
            resetCamera(startPanoramaData.defaultLookAtDirection);
        }
    }, [startPanorama, panoramaTour, loading]);

    return <div style={{position: "absolute", height: "100%", width: "100%"}} id="root">
      { panoramaTour && stage === "entry" &&
          <EntryScreen title={panoramaTour.name} roomCount={panoramaTour.rooms.length}
                       pictureUrls={startRoomImages} startTour={startTour} />
      }
      { panoramaTour && currentPanorama && startPanorama && stage === "tour" && <>
          {!isVrMode && <BrowserUI floorPlans={panoramaTour.floorPlans} rooms={panoramaTour.rooms} changePanorama={changePanorama} currentPanorama={currentPanorama} isSceneReady={!loading} />}

          <PanoramaScene tour={panoramaTour} currentPanorama={currentPanorama} startPanorama={startPanorama} changePanorama={changePanorama}
                         sceneLoaded={() => setLoading(false)}
                         setIsVrMode={setIsVrMode} isVrMode={isVrMode}
                    />
      </>
      }
      {/* Only the scene is worth waiting for, and it is not mounted until the tour is asked for. */}
      <Backdrop
          open={stage === "tour" ? loading : !panoramaTour}
          sx={{ bgcolor: 'rgba(255, 255, 255, 1)', color: '#fff', zIndex: 999999}}
      >
          {/* Stacked where there is no room for a 100px spinner and a heading side by side. */}
          <Stack direction={viewportWidth === ViewportWidth.Narrow ? "column" : "row"}
                 spacing={4} alignItems={"center"}>
            <CircularProgress size={100} /> <Typography variant={"h4"} color={"black"}>
              {stage === "tour" ? "Starting tour..." : "Loading tour..."}
          </Typography>
          </Stack>
      </Backdrop>

    </div>
}

export default App;

// URL param `defaultsky` overrides the tour's own default; both are validated against the panorama list
// so a stale name cannot strand the viewer. Falls back to the export's first viewpoint.
function chooseStartPanorama(tour: PanoramaTour, urlDefault: string | null) {
    const preferred = [urlDefault, tour.defaultStartView];
    return preferred.find(name => tour.panoramas.some(p => p.name === name))
        ?? tour.panoramas[0]?.name;
}

// Returns room image candidates in priority order: start view's room first, then the rest. EntryScreen
// tries them in sequence and shows the first that loads — a DisplayName doesn't guarantee a room image.
function entryRoomImageCandidates(tour: PanoramaTour, startPanorama: string | undefined) {
    const startRoom = tour.rooms.find(room => room.targetPanorama === startPanorama);
    const roomsStartFirst = startRoom ? [startRoom, ...tour.rooms.filter(r => r !== startRoom)] : tour.rooms;
    return roomsStartFirst.map(room => room.image);
}
